import React from "react";
import { TCanvasNode } from "./types/TCanvasNode";
import "./Canvas.css";
import { TCanvasLink } from "./types/TCanvasLink";
import Canvas from "./components/Canvas";
import { TCatalog } from "./types/TCatalog";
import parseNodes from "./utils/parseNodes";
import { TCanvasHandler } from "./types/TCanvasHandler";
import { DEFAULT_ACCURACY_RANGE, DEFAULT_SEARCH_TEXT, SEARCH_TYPE, DEFAULT_SHOW_DQ_TOGGLE, SEARCH_DISPLAY_MODE } from "./config";
import updateNodes from "./utils/updateNodes";
import { SearchDisplayMode, SearchType, TCanvasOptions } from "./types/TCanvasOptions";
import updateLinks from "./utils/updateLinks";
import parseLinks from "./utils/parseLinks";
import { TSemanticCanvas } from "./types/TSemanticCanvas";
import { TRenderActionType, TRenderEvent, TRenderPayload } from "./types/TRenderEvent";
import { TSelectionListItem } from "./types/TSelectionListItem";
import SelectionFilter from "./components/SelectionFilter";
import useDebounced from "./hooks/useDebounced";
import GranularitySlider from "./components/CanvasOptions/GranularitySlider";
import useUpdateState from "./hooks/useUpdateState";
import getConnectionCountById from "./utils/getConnectionCountById";
import SearchBar from "./components/CanvasOptions/SearchBar";
import SocketsProvider from "./context/SocketProvider";
import TransitionProvider from "./context/TransitionProvider";
import { cn } from "../lib/utils";
import useFitContextMenuToScreen from "../hooks/useFitContextMenuToScreen";


const SemanticCanvas = React.forwardRef(
  (
    props: TSemanticCanvas,
    ref: React.ForwardedRef<TCanvasHandler | null>
  ) => {

    // all entites that have added and remove in entities
    const [nodes, setNodes] = React.useState<TCanvasNode[]>([])
    const [links, setLinks] = React.useState<TCanvasLink[]>([])

    React.useEffect(() => {
      let _nodes = parseNodes(props.initialNodes ?? [])
      setNodes(_nodes)
    }, [props.initialNodes])


    React.useEffect(() => {
      setLinks(parseLinks(props.initialLinks ?? []))
    }, [props.initialLinks])

    const nodesById = React.useMemo(() => {
      return Object.fromEntries(nodes.map(item => ([item.node_id, item])))
    }, [nodes])

    const { domainsById, tablesById, columnsById } = React.useMemo(() => {
      const dl: [string, TCanvasNode][] = []
      const tl: [string, TCanvasNode][] = []
      const cl: [string, TCanvasNode][] = []
      nodes.forEach(node => {
        if (node.node_type == "DOMAIN") dl.push([node.node_id, node])
        else if (node.node_type == "TABLE") tl.push([node.node_id, node])
        else if (node.node_type == "COLUMN") cl.push([node.node_id, node])
        else throw new Error("Unknown node type")
      })
      return {
        domainsById: Object.fromEntries(dl),
        tablesById: Object.fromEntries(tl),
        columnsById: Object.fromEntries(cl)
      }
    }, [nodes])

    const selectionList = React.useMemo(() => {
      const domainListMap = new Map<string, TSelectionListItem>()
      for (const [key, value] of Object.entries(domainsById)) {
        domainListMap.set(key, {
          ...value,
          children: [],
        })
      }
      nodes.forEach(item => {
        if (item.node_type != "TABLE") return;
        if (item.parent_node_id && domainListMap.has(item.parent_node_id)) {
          const prevItemKey = item.parent_node_id
          const prevItem = domainListMap.get(item.parent_node_id)
          prevItem && domainListMap.set(prevItemKey, {
            ...prevItem,
            children: [
              ...(prevItem.children ?? []),
              {
                ...item,
                children: null
              }
            ]
          })
        } else {
          domainListMap.set(item.node_id, {
            ...item,
            children: null
          })
        }
      })
      const resultArray = Array.from(domainListMap, ([_, value]) => (value))
      return resultArray;
    }, [nodes, domainsById])

    const linksById = React.useMemo(() => {
      return Object.fromEntries(links.map(item => ([`${item.source}__${item.target}`, item])))
    }, [links])


    const connectionsById = React.useMemo(() => getConnectionCountById(links), [links])

    // canvas render nodes and links
    const [renderNodes, setRenderNodes] = React.useState<TCanvasNode[]>(nodes)
    const [renderLinks, setRenderLinks] = React.useState<TCanvasLink[]>(links)

    const renderNodesById = React.useMemo(() => {
      return Object.fromEntries(renderNodes.map(item => ([item.node_id, item])))
    }, [renderNodes])

    // ref to store all canvas options values
    const renderRef = React.useRef<HTMLDivElement>(null)
    const canvasOptionsRef = React.useRef<TCanvasOptions | null>(null)

    // states of options to render ui
    const [granularity, setGranularity] = React.useState<number>(props.defaultGranularity ?? 2)
    const [nodeDistance, setNodeDistance] = React.useState<number>(props.nodeDistance ?? 1.0)
    const [nodeRepulsion, setNodeRepulsion] = React.useState<number>(props.nodeRepulsion ?? 1.0)
    const [maxNodes, setMaxNodes] = React.useState<number>(props.maxShowingColumnCount ?? 4)
    const [inputSearchText, setInputSearchText] = React.useState<string>(props.defaultSearchText ?? DEFAULT_SEARCH_TEXT);
    const { debouncedValue: searchText } = useDebounced(inputSearchText, 300);
    const [accuracy, setAccuracy] = React.useState({
      min: props.defaultAccuracyRange ? props.defaultAccuracyRange[0] : DEFAULT_ACCURACY_RANGE[0],
      max: props.defaultAccuracyRange ? props.defaultAccuracyRange[1] : DEFAULT_ACCURACY_RANGE[1]
    })
    const [showDQ, setShowDQ] = React.useState<boolean>(props.defaultShowDQ ?? DEFAULT_SHOW_DQ_TOGGLE)
    const [hiddenList, setHiddenList] = React.useState<string[]>([])
    const [openSelectionFilter, setOpenSelectionFilter] = React.useState(false)
    const [filterItems, setFilterItems] = React.useState<number[] | null>(null)
    const [searchType, setSearchType] = React.useState<SearchType>(props.searchType ?? SEARCH_TYPE);
    const [searchDisplayMode, setSearchDisplayMode] = React.useState<SearchDisplayMode>(props.searchDisplayMode ?? SEARCH_DISPLAY_MODE);

    useUpdateState(props.defaultGranularity, setGranularity)
    useUpdateState(props.defaultShowDQ, setShowDQ)
    useUpdateState(props.nodeDistance, setNodeDistance)
    useUpdateState(props.maxShowingColumnCount, setMaxNodes)
    useUpdateState(props.nodeRepulsion, setNodeRepulsion)


    // update canvasOptionsRef on canvas options states change
    React.useEffect(() => {
      canvasOptionsRef.current = {
        granularity,
        minAccuracy: accuracy.min,
        maxAccuracy: accuracy.max,
        searchText: inputSearchText,
        expandOthersNode: props.expandOthersNode ?? true,
        showDQ,
        nodeDistance: nodeDistance,
        nodeRepulsion: nodeRepulsion,
        searchType: searchType,
        searchDisplayMode: searchDisplayMode,
        filterItems: filterItems,
        maxShowingColumnCount: maxNodes ?? null,
        hiddenList,
      }
    }, [
      granularity,
      nodeDistance,
      nodeRepulsion,
      inputSearchText,
      accuracy,
      showDQ,
      hiddenList,
      props.searchType,
      filterItems,
      searchType,
      searchDisplayMode,
      maxNodes
    ])


    const updateGraph = () => {
      const _newRenderNodes = updateNodes(domainsById, tablesById, columnsById, renderNodesById, links, connectionsById, canvasOptionsRef.current)
      
      // Only add default domain node if there are actual nodes to render
      // This prevents the default domain from blocking the background particles on empty canvas
      if (_newRenderNodes.length > 0 || Object.keys(tablesById).length > 0) {
        const defaultDomainNode: TCanvasNode =  {
          node_id: "__DEFAULT_DOMAIN__",
          node_type: "DEFAULT_DOMAIN",
          id: "__DEFAULT_DOMAIN__",
          name: "__DEFAULT__",
          parent_node_id: null,
          description: ""
        }
        _newRenderNodes.push(defaultDomainNode)
      }
      
      setRenderNodes(_newRenderNodes)
      const prevNodesById = Object.fromEntries(_newRenderNodes.map(item => [item.node_id, item]))
      setRenderLinks(updateLinks(nodesById, linksById, connectionsById, prevNodesById, canvasOptionsRef?.current))
    }

    // when base nodes and links
    React.useEffect(() => {
      // transform and apply canvas options and make render nodes and render links
      updateGraph()
    }, [nodes, links, accuracy, granularity, hiddenList, nodeDistance, maxNodes, nodeRepulsion, filterItems, searchType])

    React.useEffect(() => {
      if (canvasOptionsRef.current?.searchDisplayMode == SearchDisplayMode.FILTER) {
        updateGraph()
      } else if (canvasOptionsRef.current?.searchText == searchText && (props.expandOthersNode ?? true)) {
        updateGraph()
      }
    }, [inputSearchText, searchText, searchDisplayMode])

    const [renderEvent, setRenderEvent] = React.useState<TRenderEvent | null>(null)


    const addNodes = React.useCallback((...newNodes: TCatalog[]) => {
      if (newNodes == undefined || newNodes.length == 0) return;
      const _nodes = parseNodes(newNodes)
      setNodes((prev) => [...prev, ..._nodes])
    }, [])

    const removeNodes = React.useCallback((...nodeIds: string[]) => {
      if (nodeIds == undefined || nodeIds.length == 0) return;
      const nodeIdsSet = new Set(nodeIds)
      setNodes((prev) => prev.filter(node => !nodeIdsSet.has(node.node_id)))
    }, [])


    React.useImperativeHandle(ref, () => {
      return {
        add_nodes: addNodes,
        remove_nodes: removeNodes,
        add_links: () => { },
        remove_links: () => { },
        updateNodes: () => { },
      }
    }, [addNodes, removeNodes])


    const updateRenderEvent = React.useCallback((
      event?: MouseEvent,
      action_type?: TRenderActionType,
      payload?: TRenderPayload,
    ) => {
      let action;
      switch (action_type) {
        case "canvas-right": {
          action = props.canvasRightClickRender;
          break;
        }
        case "node-right": {
          action = props.nodeRightClickRender;
          break;
        }
        case "node-left": {
          action = props.nodeLeftClickRender;
          break;
        }
        case "link-right": {
          action = props.linkRightClickRender;
          break;
        }
        case "link-left": {
          action = props.linkRightClickRender;
          break;
        }
      }

      if (action == undefined || action_type == undefined || event == undefined) {
        setRenderEvent(null)
        props.onRenderClose && props.onRenderClose()
        return;
      } else {
        setRenderEvent({
          action,
          action_type,
          payload: {
            ...payload,
            opts: {
              connectionsById: connectionsById,
              nodesById: nodesById
            }
          },
          position: event ? {
            x: event.clientX,
            y: event.clientY,
          } : undefined
        })
      }
    }, [connectionsById, nodesById])

    useFitContextMenuToScreen(renderRef, [renderEvent])

    return (
      <SocketsProvider>
        <TransitionProvider>
        <div className="w-full h-full relative">
          <Canvas
            width={props.width}
            height={props.height}
            nodes={renderNodes}
            links={renderLinks}
            canvasOptionRef={canvasOptionsRef}
            updateRenderEvent={updateRenderEvent}
            physicsParams={props.physicsParams}
            shouldReheatSimulation={props.shouldReheatSimulation}
            highlightedNodeIds={props.highlightedNodeIds}
          />
          {
            props.showSearchBar && (
              <div className={cn("absolute flex flex-col gap-2 top-8 right-7 w-[300px] bg-transparent", props.searchBarStyle)}>
                <SearchBar
                  searchType={searchType}
                  setSearchType={setSearchType}
                  setFilterItems={setFilterItems}
                  inputSearchText={inputSearchText} 
                  setInputSearchText={setInputSearchText}
                  setOpenSelectionFilter={setOpenSelectionFilter}
                  openSelectionFilter={openSelectionFilter}
                  searchDisplayMode={searchDisplayMode}
                  setSearchDisplayMode={setSearchDisplayMode}
                  
                />
                {
                  openSelectionFilter && <SelectionFilter hiddenList={hiddenList} setHiddenList={setHiddenList} selectionList={selectionList} />
                }
              </div>
            )
          }
          <div className="absolute bottom-6 right-7 z-10">
            <GranularitySlider showGranularity={props.showGranularity ?? true} granularity={granularity} setGranularity={setGranularity} />
          </div>

          {
            renderEvent && (
              <>
                {/* Backdrop overlay for link and node popups */}
                {(renderEvent.action_type === 'link-left' || renderEvent.action_type === 'link-right' || renderEvent.action_type === 'node-left' || renderEvent.action_type === 'node-right') && (
                  <div 
                    className="fixed inset-0 bg-black/60 z-40"
                    onClick={() => updateRenderEvent(undefined, "canvas-left", {})}
                  />
                )}
                <div
                  ref={renderRef}
                  className="transition-transform duration-200 z-50"
                  style={{
                    position: "fixed",
                    top: `${renderEvent.position?.y}px`,
                    left: `${renderEvent.position?.x}px`
                  }}
                >
                  {
                    renderEvent.action
                    && <renderEvent.action containerRef={renderRef} {...renderEvent.payload} onClose={() => updateRenderEvent(undefined, "canvas-left", {})}/>
                  }
                </div>
              </>
            )
          }
        </div>
      </TransitionProvider>
      </SocketsProvider>
    )
  })

export default SemanticCanvas;
